import { useCallback, useEffect, useRef, useState } from "react";
import { MAX_QUESTION_CHARS, type HandbookAskRequest, type HandbookAskResponse, type TrainingError } from "@carbon-os/server/contract";
import { trackHandbook, type HandbookEvents } from "./analytics";
import { browserDeps } from "./api";
import { sessionFetch } from "./session";
import {
  beginSignIn,
  clearSignInAttempt,
  HANDBOOK_QUESTION_KEY,
  HANDBOOK_SIGNIN_ATTEMPT_KEY,
  shouldRedirect,
} from "./signin";

/**
 * Asking the Company handbook a question (carbon-gatekeeper#246). Gatekeeper
 * finds the passages and writes the answer; the page shows what comes back and
 * adds no words of its own. Each question is its own request, one at a time,
 * and nothing is kept between them.
 */

/** The page gives up after this, whatever the server is still doing. */
export const ASK_TIMEOUT_MS = 30_000;

/** Ask turns on from here: under it, or over the limit, nothing is sent. */
export function questionLength(text: string): number {
  return text.trim().length;
}

export function isAskable(text: string): boolean {
  const length = questionLength(text);
  return length > 0 && length <= MAX_QUESTION_CHARS;
}

export type AskResult =
  /** Gatekeeper's answer, whichever its outcome: answered, not covered, or passages without an answer. */
  | { kind: "answer"; response: HandbookAskResponse }
  | { kind: "rate_limited"; minutes: number | null }
  /** Gatekeeper is down. */
  | { kind: "unavailable" }
  /** A timeout, a request that never arrived, or anything else unexpected. */
  | { kind: "error" }
  /** A Gatekeeper from before questions: the ask box goes. */
  | { kind: "unsupported" }
  | { kind: "needs_signin" };

const OUTCOMES: readonly HandbookAskResponse["outcome"][] = ["answered", "not_covered", "answer_unavailable"];

function isAnswer(body: unknown): body is HandbookAskResponse {
  if (typeof body !== "object" || body === null) return false;
  const { outcome, answer, sources, related } = body as Partial<HandbookAskResponse>;
  return OUTCOMES.includes(outcome as HandbookAskResponse["outcome"]) && Array.isArray(sources) && Array.isArray(related)
    && (outcome === "answered" ? Array.isArray(answer?.sentences) : answer === null);
}

/** One question, as one POST. Only a 200 is ever an answer, and only an answered one has prose. */
export async function askHandbook(question: string, fetchImpl: typeof fetch): Promise<AskResult> {
  const request: HandbookAskRequest = { question };
  let res: Response;
  let body: unknown;
  try {
    res = await fetchImpl("/api/handbook/ask", {
      method: "POST",
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(ASK_TIMEOUT_MS),
    });
    body = await res.json().catch(() => null);
  } catch {
    return { kind: "error" };
  }
  const error = (typeof body === "object" && body !== null ? body : {}) as Partial<TrainingError>;
  if (res.status === 200) return isAnswer(body) ? { kind: "answer", response: body } : { kind: "error" };
  if (res.status === 401) return { kind: "needs_signin" };
  if (res.status === 404 && error.code === "search_unsupported") return { kind: "unsupported" };
  if (res.status === 429) {
    const minutes = error.retryAfterMinutes;
    return { kind: "rate_limited", minutes: Number.isInteger(minutes) && (minutes as number) >= 0 ? (minutes as number) : null };
  }
  if (res.status === 503 && error.code === "search_unavailable") return { kind: "unavailable" };
  return { kind: "error" };
}

/** What a result counts as in `handbook_question_asked`; null for the ones never counted. `retry` for Try again. */
export function askEvent(result: AskResult, durationMs: number, retry = false): HandbookEvents["handbook_question_asked"] | null {
  if (result.kind === "needs_signin" || result.kind === "unsupported") return null;
  if (result.kind === "answer") {
    const { response } = result;
    const shown = response.outcome === "answered" ? response.sources.length : response.related.length;
    return { outcome: response.outcome, source_count: shown, duration_ms: Math.round(durationMs), retry };
  }
  return { outcome: result.kind === "rate_limited" ? "rate_limited" : "error", source_count: 0, duration_ms: Math.round(durationMs), retry };
}

export type AskView =
  | { kind: "idle" }
  /** `question` is the one submitted, which the box may no longer hold. */
  | { kind: "loading"; question: string }
  | (Exclude<AskResult, { kind: "unsupported" }> & { question: string })
  | { kind: "signin_failed"; question: string }
  /** Found out by asking: the box is gone for as long as the page is open. */
  | { kind: "unsupported" };

export interface AskControls {
  view: AskView;
  /** What the box holds. Nothing but Clear ever empties it. */
  text: string;
  setText: (text: string) => void;
  /** Sends the question, unless one is already out or it is empty or too long. `retry` when it is Try again. */
  ask: (question: string, options?: { retry?: boolean }) => void;
  clear: () => void;
  /** Keeps the box's question for the trip to sign-in, then goes. */
  signIn: () => void;
  /** Goes up by one for each result shown, so the page can bring it into view. */
  results: number;
}

function savedQuestion(): string {
  try {
    return window.sessionStorage.getItem(HANDBOOK_QUESTION_KEY) ?? "";
  } catch {
    return "";
  }
}

/**
 * The ask box and its result. It lives in the Handbook page, which stays
 * mounted while a cited document is read, so the answer is still there on
 * the way back; a reload forgets it.
 */
export function useHandbookAsk(): AskControls {
  const [view, setView] = useState<AskView>({ kind: "idle" });
  // A question kept for sign-in comes back into the box, once, unsent.
  const [text, setText] = useState(savedQuestion);
  const [results, setResults] = useState(0);
  const pending = useRef(false);

  useEffect(() => {
    try {
      window.sessionStorage.removeItem(HANDBOOK_QUESTION_KEY);
    } catch {
      // Nothing was kept, then.
    }
  }, []);

  const ask = useCallback((raw: string, { retry = false }: { retry?: boolean } = {}) => {
    const question = raw.trim();
    if (pending.current || !isAskable(question)) return;
    pending.current = true;
    setView({ kind: "loading", question });
    const started = performance.now();
    void askHandbook(question, (...args) => sessionFetch(...args)).then((result) => {
      pending.current = false;
      if (result.kind === "unsupported") {
        setView({ kind: "unsupported" });
        return;
      }
      if (result.kind === "answer") {
        try {
          clearSignInAttempt(window.sessionStorage, HANDBOOK_SIGNIN_ATTEMPT_KEY);
        } catch {
          // Blocked storage holds no loop marker to clear; the answer still shows.
        }
      }
      setView({ ...result, question });
      setResults((count) => count + 1);
      const event = askEvent(result, performance.now() - started, retry);
      if (event) trackHandbook("handbook_question_asked", event);
    });
  }, []);

  const clear = useCallback(() => {
    if (pending.current) return;
    setView({ kind: "idle" });
    setText("");
  }, []);

  const signIn = useCallback(() => {
    const deps = browserDeps();
    if (!shouldRedirect(deps.now, deps.storage, HANDBOOK_SIGNIN_ATTEMPT_KEY)) {
      setView((current) => (current.kind === "needs_signin" ? { kind: "signin_failed", question: current.question } : current));
      return;
    }
    try {
      // The question that was asked, not whatever the box holds now.
      deps.storage.setItem(HANDBOOK_QUESTION_KEY, view.kind === "needs_signin" ? view.question : text);
    } catch {
      // Blocked storage: sign-in still goes ahead, without the question.
    }
    beginSignIn(deps.returnTo, deps.storage, deps.navigate, deps.now, HANDBOOK_SIGNIN_ATTEMPT_KEY);
  }, [text, view]);

  return { view, text, setText, ask, clear, signIn, results };
}
